// Advanced Offline Storage System for AgroSense
// Comprehensive offline-first data management

interface StorageConfig {
  dbName: string;
  version: number;
  stores: Record<string, {
    keyPath: string;
    autoIncrement?: boolean;
    indexes?: Record<string, string | string[]>;
  }>;
}

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  expiry: number;
  version: string;
  checksum?: string;
}

interface SyncAction {
  id?: number;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  endpoint: string;
  data: any;
  timestamp: number;
  retries: number;
  maxRetries: number;
  priority: 'high' | 'medium' | 'low';
}

class AdvancedOfflineStorage {
  private db: IDBDatabase | null = null;
  private config: StorageConfig;
  private syncQueue: SyncAction[] = [];
  private isOnline: boolean = navigator.onLine;

  constructor() {
    this.config = {
      dbName: 'AgroSenseAdvancedDB',
      version: 2,
      stores: {
        // User data
        users: {
          keyPath: 'id',
          indexes: {
            email: 'email',
            lastSync: 'lastSync'
          }
        },
        // Farm data
        farms: {
          keyPath: 'id',
          indexes: {
            userId: 'userId',
            location: 'location',
            createdAt: 'createdAt'
          }
        },
        // Crop health reports
        cropReports: {
          keyPath: 'id',
          indexes: {
            farmId: 'farmId',
            userId: 'userId',
            timestamp: 'timestamp',
            cropType: 'cropType'
          }
        },
        // Weather data cache
        weatherCache: {
          keyPath: 'location',
          indexes: {
            timestamp: 'timestamp',
            expiry: 'expiry'
          }
        },
        // Market prices cache
        marketCache: {
          keyPath: 'commodity',
          indexes: {
            timestamp: 'timestamp',
            market: 'market'
          }
        },
        // Sync queue for offline actions
        syncQueue: {
          keyPath: 'id',
          autoIncrement: true,
          indexes: {
            timestamp: 'timestamp',
            priority: 'priority',
            type: 'type'
          }
        },
        // App settings and preferences
        settings: {
          keyPath: 'key',
          indexes: {
            userId: 'userId'
          }
        },
        // Cached API responses
        apiCache: {
          keyPath: 'url',
          indexes: {
            timestamp: 'timestamp',
            expiry: 'expiry',
            endpoint: 'endpoint'
          }
        }
      }
    };

    this.init();
    this.setupNetworkListeners();
  }

  private async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.config.dbName, this.config.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        this.loadSyncQueue();
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object stores
        Object.entries(this.config.stores).forEach(([storeName, config]) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, {
              keyPath: config.keyPath,
              autoIncrement: config.autoIncrement || false
            });

            // Create indexes
            if (config.indexes) {
              Object.entries(config.indexes).forEach(([indexName, keyPath]) => {
                store.createIndex(indexName, keyPath, { unique: false });
              });
            }
          }
        });
      };
    });
  }

  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // Generic CRUD operations
  async create<T>(storeName: string, data: T): Promise<T> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.add(data);
      request.onsuccess = () => resolve(data);
      request.onerror = () => reject(request.error);
    });
  }

  async read<T>(storeName: string, key: any): Promise<T | null> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async update<T>(storeName: string, data: T): Promise<T> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.put(data);
      request.onsuccess = () => resolve(data);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName: string, key: any): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAll<T>(storeName: string, indexName?: string, query?: any): Promise<T[]> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      
      let request: IDBRequest;
      if (indexName && query) {
        const index = store.index(indexName);
        request = index.getAll(query);
      } else {
        request = store.getAll();
      }
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Advanced caching with expiry
  async cacheData<T>(key: string, data: T, maxAge: number = 5 * 60 * 1000): Promise<void> {
    const cacheEntry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + maxAge,
      version: '1.0',
      checksum: this.generateChecksum(data)
    };

    await this.update('apiCache', { url: key, ...cacheEntry });
  }

  async getCachedData<T>(key: string): Promise<T | null> {
    const cached = await this.read<CacheEntry<T>>('apiCache', key);
    
    if (!cached || cached.expiry < Date.now()) {
      return null;
    }
    
    return cached.data;
  }

  // Sync queue management
  async addToSyncQueue(action: Omit<SyncAction, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    const syncAction: SyncAction = {
      ...action,
      timestamp: Date.now(),
      retries: 0,
      maxRetries: action.priority === 'high' ? 5 : 3
    };

    await this.create('syncQueue', syncAction);
    this.syncQueue.push(syncAction);

    if (this.isOnline) {
      this.processSyncQueue();
    }
  }

  private async loadSyncQueue(): Promise<void> {
    this.syncQueue = await this.getAll<SyncAction>('syncQueue');
  }

  private async processSyncQueue(): Promise<void> {
    if (!this.isOnline || this.syncQueue.length === 0) return;

    // Sort by priority and timestamp
    const sortedQueue = [...this.syncQueue].sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      return priorityDiff !== 0 ? priorityDiff : a.timestamp - b.timestamp;
    });

    for (const action of sortedQueue) {
      try {
        await this.executeSync(action);
        await this.removeSyncAction(action.id!);
        this.syncQueue = this.syncQueue.filter(a => a.id !== action.id);
      } catch (error) {
        console.error('Sync failed for action:', action.id, error);
        await this.handleSyncFailure(action);
      }
    }
  }

  private async executeSync(action: SyncAction): Promise<void> {
    const method = action.type === 'DELETE' ? 'DELETE' : 
                  action.type === 'UPDATE' ? 'PUT' : 'POST';

    const response = await fetch(action.endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.getAuthHeader()
      },
      body: action.type !== 'DELETE' ? JSON.stringify(action.data) : undefined
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  private async handleSyncFailure(action: SyncAction): Promise<void> {
    action.retries++;
    
    if (action.retries >= action.maxRetries) {
      // Move to failed queue or delete
      await this.removeSyncAction(action.id!);
      this.syncQueue = this.syncQueue.filter(a => a.id !== action.id);
      console.warn('Max retries reached for sync action:', action.id);
    } else {
      // Update retry count
      await this.update('syncQueue', action);
    }
  }

  private async removeSyncAction(id: number): Promise<void> {
    await this.delete('syncQueue', id);
  }

  // Utility methods
  private generateChecksum(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      // Use TextEncoder to handle Unicode characters properly
      const encoder = new TextEncoder();
      const bytes = encoder.encode(jsonString);
      
      // Simple hash function for checksum
      let hash = 0;
      for (let i = 0; i < bytes.length; i++) {
        hash = ((hash << 5) - hash + bytes[i]) & 0xffffffff;
      }
      
      return Math.abs(hash).toString(16).slice(0, 16);
    } catch (error) {
      // Fallback to simple timestamp-based checksum
      return Date.now().toString(16).slice(-8);
    }
  }

  private getAuthHeader(): string {
    // Get auth token from localStorage or other secure storage
    const token = localStorage.getItem('auth_token');
    return token ? `Bearer ${token}` : '';
  }

  // Data synchronization methods
  async syncUserData(userId: string): Promise<void> {
    if (!this.isOnline) return;

    try {
      // Sync farms
      const farms = await this.getAll('farms', 'userId', userId);
      for (const farm of farms) {
        if (farm.needsSync) {
          await this.addToSyncQueue({
            type: 'UPDATE',
            endpoint: `/api/farms/${farm.id}`,
            data: farm,
            priority: 'medium'
          });
        }
      }

      // Sync crop reports
      const reports = await this.getAll('cropReports', 'userId', userId);
      for (const report of reports) {
        if (report.needsSync) {
          await this.addToSyncQueue({
            type: 'CREATE',
            endpoint: '/api/crop-reports',
            data: report,
            priority: 'high'
          });
        }
      }
    } catch (error) {
      console.error('Sync failed:', error);
    }
  }

  // Clean expired data
  async cleanExpiredData(): Promise<void> {
    const now = Date.now();
    
    // Clean expired cache entries
    const expiredCache = await this.getAll<any>('apiCache');
    for (const entry of expiredCache) {
      if (entry.expiry < now) {
        await this.delete('apiCache', entry.url);
      }
    }

    // Clean old weather data (keep last 7 days)
    const weekAgo = now - (7 * 24 * 60 * 60 * 1000);
    const oldWeather = await this.getAll<any>('weatherCache');
    for (const entry of oldWeather) {
      if (entry.timestamp < weekAgo) {
        await this.delete('weatherCache', entry.location);
      }
    }
  }

  // Export/Import for backup
  async exportData(): Promise<string> {
    const data: Record<string, any[]> = {};
    
    for (const storeName of Object.keys(this.config.stores)) {
      data[storeName] = await this.getAll(storeName);
    }
    
    return JSON.stringify({
      version: this.config.version,
      timestamp: Date.now(),
      data
    });
  }

  async importData(jsonData: string): Promise<void> {
    const backup = JSON.parse(jsonData);
    
    for (const [storeName, items] of Object.entries(backup.data)) {
      for (const item of items as any[]) {
        await this.update(storeName, item);
      }
    }
  }

  // Get storage statistics
  async getStorageStats(): Promise<{
    totalSize: number;
    storeStats: Record<string, number>;
    syncQueueSize: number;
  }> {
    const stats: Record<string, number> = {};
    let totalSize = 0;
    
    for (const storeName of Object.keys(this.config.stores)) {
      const items = await this.getAll(storeName);
      const size = JSON.stringify(items).length;
      stats[storeName] = size;
      totalSize += size;
    }
    
    return {
      totalSize,
      storeStats: stats,
      syncQueueSize: this.syncQueue.length
    };
  }
}

// Singleton instance
export const advancedOfflineStorage = new AdvancedOfflineStorage();

// Service Worker integration
export class ServiceWorkerManager {
  private sw: ServiceWorker | null = null;

  async register(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        this.sw = registration.active;
        
        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                this.notifyUpdate();
              }
            });
          }
        });
        
        console.log('Service Worker registered successfully');
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  async sendMessage(message: any): Promise<any> {
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => resolve(event.data);
      
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message, [messageChannel.port2]);
      }
    });
  }

  async storeOfflineAction(action: any): Promise<void> {
    await this.sendMessage({
      type: 'STORE_OFFLINE_ACTION',
      data: action
    });
  }

  private notifyUpdate(): void {
    // Show update notification to user
    const event = new CustomEvent('sw-update-available');
    window.dispatchEvent(event);
  }
}

export const swManager = new ServiceWorkerManager();